import { html, css, LitElement } from 'lit-element';
import { t, Localized } from './i18n.js';
import '@material/mwc-dialog';
import '@material/mwc-textfield';
import '@material/mwc-button';
import '@material/mwc-list/mwc-list-item';
import '@material/mwc-list';
import { getFirestore, collection, getDocs } from "firebase/firestore";

/**
 * <intrivity-user-search>
 *
 * Usage:
 *  - Add to your page and call `open()` to show the dialog.
 *  - Listen for 'user-selected' event: event.detail.user contains the Firestore user object.
 *
 * Example:
 *   const dialog = document.createElement('intrivity-user-search');
 *   document.body.appendChild(dialog);
 *   dialog.addEventListener('user-selected', (e) => console.log(e.detail.user));
 *   dialog.open();
 */
export class IntrivityUserSearch extends Localized(LitElement) {
  static get properties() {
    return {
      openDialog: { type: Boolean },
      query: { type: String },
      results: { type: Array },
      loading: { type: Boolean },
      error: { type: String },
      selectedIndex: { type: Number }
    };
  }

  constructor() {
    super();
    this.openDialog = false;
    this.query = '';
    this.results = [];
    this.loading = false;
    this.error = '';
    this.selectedIndex = -1;
    this._debounceTimer = null;
    this.db = getFirestore(); // uses existing initialized Firebase app
  }

  static get styles() {
    return css`
      :host {
        display: block;
      }
      .results {
        max-height: 320px;
        overflow: auto;
        margin-top: 8px;
        border-radius: 4px;
        border: 1px solid rgba(0,0,0,0.08);
      }
      .row {
        display: grid;
        grid-template-columns: 48px 1fr;
        gap: 10px;
        align-items: center;
        padding: 8px;
        cursor: pointer;
      }
      .row[selected] {
        background: rgba(0,0,0,0.06);
      }
      .avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        object-fit: cover;
        background: #eee;
      }
      .meta {
        display: flex;
        flex-direction: column;
      }
      .name {
        font-weight: 600;
      }
      .email {
        font-size: 0.9rem;
        color: #666;
      }
      .no-results {
        padding: 12px;
        color: #666;
      }
      mwc-dialog {
        --mdc-dialog-min-width: 520px;
      }
      @media (max-width: 520px) {
        mwc-dialog { --mdc-dialog-min-width: 320px; }
      }
    `;
  }

  render() {
    return html`
      <mwc-dialog .open=${this.openDialog} @closing=${this._onDialogClosing} heading=${t('userSearch.heading')}>
        <div>
          <mwc-textfield
            outlined
            icon="search"
            label=${t('userSearch.label')}
            .value=${this.query}
            @input=${this._onInput}
            @keydown=${this._onKeydown}
            style="width:100%;"
          ></mwc-textfield>

          ${this.loading ? html`<div style="margin:8px 0">${t('userSearch.searching')}</div>` : html``}
          ${this.error ? html`<div style="color:var(--mdc-theme-error);margin-top:8px">${this.error}</div>` : html``}

          <div class="results" role="list">
            ${this.results && this.results.length > 0 ? html`
              ${this.results.map((u, idx) => html`
                <div
                  class="row" 
                  role="listitem"
                  ?selected=${this.selectedIndex === idx}
                  @click=${() => this._selectIndex(idx)}
                  @dblclick=${() => this._confirmSelection(idx)}
                >
                  <img class="avatar" src=${u.image ? u.image : 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"></svg>'} alt="" />
                  <div class="meta">
                    <div class="name">${u.fullName || u.displayName || (u.firstName + ' ' + (u.lastName||''))}</div>
                    <div class="email">${u.email}</div>
                  </div>
                </div>
              `)}
            ` : html`<div class="no-results">${this.loading ? '' : t('userSearch.none')}</div>`}
          </div>
        </div>

        <mwc-button slot="primaryAction" unelevated @click=${this._onSelect} ?disabled=${this.selectedIndex < 0}>${t('userSearch.select')}</mwc-button>
        <mwc-button slot="secondaryAction" @click=${this._onCancel}>${t('common.cancel')}</mwc-button>
      </mwc-dialog>
    `;
  }

  // PUBLIC API: open the dialog (and clear previous state)
  open() {
    this.query = '';
    this.results = [];
    this.selectedIndex = -1;
    this.error = '';
    this.openDialog = true;
    // wait a tick then focus the textfield inside the dialog
    requestAnimationFrame(() => {
      const tf = this.shadowRoot.querySelector('mwc-textfield');
      if (tf) tf.focus();
    });
  }

  // PUBLIC API: close
  close() {
    this.openDialog = false;
  }

  _onDialogClosing(e) {
    // keep openDialog in sync with dialog state
    // mwc-dialog emits closing with reason; actual closed state will follow
    // ensure we reflect closed state
    this.openDialog = false;
  }

  _onCancel() {
    this.close();
  }

  _onSelect() {
    if (this.selectedIndex < 0 || !this.results[this.selectedIndex]) return;
    this._confirmSelection(this.selectedIndex);
  }

  _confirmSelection(idx) {
    const user = this.results[idx];
    // emit selected event with full user object
    this.dispatchEvent(new CustomEvent('user-selected', { detail: { user }, bubbles: true, composed: true }));
    this.close();
  }

  _selectIndex(idx) {
    this.selectedIndex = idx;
    this.requestUpdate();
  }

  _onInput(e) {
    this.query = e.target.value || '';
    // debounce search so we don't hammer Firestore
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this._performSearch(), 300);
  }

  _onKeydown(e) {
    // Enter to select (if an item is highlighted), ArrowUp/Down to change selection
    if (e.key === 'Enter') {
      e.preventDefault();
      if (this.selectedIndex >= 0) this._confirmSelection(this.selectedIndex);
      else this._performSearch();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (this.results && this.results.length > 0) {
        this.selectedIndex = Math.min((this.selectedIndex + 1), this.results.length - 1);
        this.requestUpdate();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (this.results && this.results.length > 0) {
        this.selectedIndex = Math.max((this.selectedIndex - 1), 0);
        this.requestUpdate();
      }
    }
  }

  async _performSearch() {
    const q = (this.query || '').trim();
    if (!q) {
      this.results = [];
      this.error = '';
      this.selectedIndex = -1;
      this.requestUpdate();
      return;
    }

    this.loading = true;
    this.error = '';
    this.results = [];
    this.selectedIndex = -1;
    this.requestUpdate();

    try {
      // NOTE: For simplicity we fetch all users and filter client-side.
      // If you have many users, replace this with server-side queries / a search index.
      const usersCol = collection(this.db, 'users');
      const snap = await getDocs(usersCol);
      const term = q.toLowerCase();

      // Map and filter documents
      const matches = [];
      snap.forEach(doc => {
        const data = doc.data();
        // normalize fields
        const first = (data.firstName || '').toString();
        const last = (data.lastName || '').toString();
        const full = (data.fullName || data.displayName || (first + ' ' + last) || '').toString();
        const email = (data.email || '').toString();
        const display = (data.displayName || '').toString();

        const hay = (first + ' ' + last + ' ' + full + ' ' + display + ' ' + email).toLowerCase();

        if (hay.indexOf(term) !== -1) {
          // include id so caller can reference doc id if needed
          matches.push({ ...data, _id: doc.id });
        }
      });

      // sort matches by relevance (simple: place ones where query appears earlier first)
      matches.sort((a, b) => {
        const aHay = ((a.fullName || a.displayName || '') + ' ' + (a.email || '')).toLowerCase();
        const bHay = ((b.fullName || b.displayName || '') + ' ' + (b.email || '')).toLowerCase();
        const ai = aHay.indexOf(term);
        const bi = bHay.indexOf(term);
        return (ai === -1 ? 9999 : ai) - (bi === -1 ? 9999 : bi);
      });

      this.results = matches;
      this.loading = false;
      this.requestUpdate();
    } catch (err) {
      console.error('User search failed', err);
      this.error = t('userSearch.failed');
      this.loading = false;
      this.requestUpdate();
    }
  }
}

customElements.define('intrivity-user-search', IntrivityUserSearch);
export default IntrivityUserSearch;
